import { useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useCarrito } from "../context/CarritoContext";
import { X, PackageCheck} from "lucide-react";



const formatAr = (n) =>
  n == null
    ? "—"
    : Number(n).toLocaleString("es-AR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

export default function CartDrawer() {
  const {
    carrito,
    setCarrito,
    subtotal, // no lo usamos para totales, lo dejo por compat
    isCartOpen,
    closeCart,
  } = useCarrito();

  const navigate = useNavigate();

  const eliminarDelCarrito = (index) => {
    setCarrito((prev) => prev.filter((_, i) => i !== index));
  };

  // 🔹 Parser robusto para strings tipo "$120.000,50" o "85.00"
  const parsePrecio = (v) => {
    if (typeof v === "number") return v;
    if (v == null) return 0;
    // quita todo salvo dígitos, coma y punto
    const s = String(v).replace(/[^\d.,-]/g, "");
    // elimina separadores de miles y usa punto como decimal
    const normalized = s.replace(/\./g, "").replace(/,/g, ".");
    const n = parseFloat(normalized);
    return Number.isFinite(n) ? n : 0;
  };

  // Bloquear scroll cuando el carrito está abierto
  useEffect(() => {
    if (!isCartOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isCartOpen]);

  // Cerrar con ESC
  useEffect(() => {
    if (!isCartOpen) return;
    const onKey = (e) => e.key === "Escape" && closeCart();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isCartOpen, closeCart]);

  const irACheckout = () => {
    closeCart();
    navigate("/checkout");
  };

  const precio30Off = (n) => Math.floor((n * 0.70) / 100) * 100;

  // ===== Totales con envío gratis =====
  const isEmpty = carrito.length === 0;
  const envioFinal = 0; // siempre gratis

  // ✅ Subtotal real = suma de (precioParseado × cantidad)
  const subtotalCalc = carrito.reduce((acc, it) => {
    const precioOriginal = parsePrecio(it.precio);
    const precioConOff = precio30Off(precioOriginal);
    const qty = Math.max(1, Number(it.cantidad ?? 1));
  return acc + precioConOff * qty;
  }, 0);

  const totalBase = isEmpty ? 0 : subtotalCalc + envioFinal;

  // 15% off (transferencia) aplicado solo al subtotal
  const totalTransfer = isEmpty ? 0 : Math.round((subtotalCalc * 0.85 + envioFinal) * 100) / 100;

  return (
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 bg-black/50 transition-opacity duration-200 z-[70] ${
          isCartOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        onClick={closeCart}
      />

      {/* Panel */}
      <aside
        className={`fixed right-0 top-0 z-[80] h-screen w-full max-w-[340px] bg-white shadow-xl
                    transition-transform duration-300 ease-out
                    ${isCartOpen ? "translate-x-0" : "translate-x-full"}
                    flex flex-col`}
        role="dialog"
        aria-modal="true"
        aria-label="Tu carrito"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="nav-onepalm text-sm text-gray-900">Tu carrito</h2>
          <button
            onClick={closeCart}
            className="p-2 rounded hover:bg-gray-100"
            aria-label="Cerrar carrito"
          >
            <X size={20} />
          </button>
        </div>

        {/* Contenido */}
        <div className="flex-1 p-8 space-y-4 overflow-y-auto">
          {carrito.length === 0 ? (
            <div className="text-center text-gray-800 pd-onepalm text-md">
              <p>Tu carrito está vacío.</p>
              <Link
                to="/productos"
                onClick={closeCart}
                className="inline-block mt-3 pd-onepalm text-gray-500 underline text-sm"
              >
                Ver productos
              </Link>
            </div>
          ) : (
            <>
              {/* ====== LISTADO ESTILO ONEPALM ====== */}
              <ul className="space-y-4">
                {carrito.map((item, i) => {
                  const qty = Math.max(1, Number(item.cantidad ?? 1));
                  return (
                    <li
                      key={i}
                      className="line-item grid grid-cols-[70px_1fr] sm:grid-cols-[120px_1fr] gap-4 items-center "
                    >
                      {/* Imagen */}
                      <img
                        src={item.img}
                        alt={item.nombre}
                        loading="lazy"
                        className="line-item__media w-[110px] h-[110px] sm:w-[120px] sm:h-[120px] object-cover"
                      />

                      {/* Info */}
                      <div className="line-item-info">
                        <div className="flex flex-col gap-2">
                          {/* Nombre + Precio unitario (se mantiene tu render) */}
                          <div className="flex flex-col gap-1">
                            <p className="h6 nav-onepalm text-[13px] leading-tight line-clamp-2">
                              {item.nombre}
                            </p>
                            <div className="price-list">
                              <span className="h6 text-gray-600 proba text-xs">
                                {typeof item.precio === "number"
                                  ? formatAr(item.precio)
                                  : item.precio}
                              </span>
                            </div>
                          </div>

                          {/* Talle en “smallcaps” */}
                          <p className="uppercase  text-[11px] tracking-[0.08em] text-gray-700">
                            talle: {item.talle}
                          </p>

                          {/* Cantidad + Quitar */}
                          <div className="flex items-center gap-4">
                            {/* Quantity selector */}
                            <div className="flex items-center border border-gray-300 rounded-sm h-8 overflow-hidden">
                              <button
                                type="button"
                                className="w-8 h-8 grid place-items-center"
                                aria-label="Disminuir cantidad"
                                onClick={() =>
                                  setCarrito((prev) =>
                                    prev.map((it, idx) =>
                                      idx === i
                                        ? {
                                            ...it,
                                            cantidad: Math.max(
                                              1,
                                              Number(it.cantidad ?? 1) - 1
                                            ),
                                          }
                                        : it
                                    )
                                  )
                                }
                              >
                                {/* icon minus */}
                                <svg
                                  aria-hidden="true"
                                  focusable="false"
                                  viewBox="0 0 12 12"
                                  width="10"
                                  className="icon icon-minus"
                                >
                                  <path
                                    d="M0 6h12"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                  ></path>
                                </svg>
                              </button>

                              <input
                                className="w-10 text-center text-sm outline-none"
                                type="number"
                                min={1}
                                value={qty}
                                onChange={(e) => {
                                  const v = Math.max(
                                    1,
                                    parseInt(e.target.value || "1", 10)
                                  );
                                  setCarrito((prev) =>
                                    prev.map((it, idx) =>
                                      idx === i ? { ...it, cantidad: v } : it
                                    )
                                  );
                                }}
                              />

                              <button
                                type="button"
                                className="w-8 h-8 grid place-items-center"
                                aria-label="Aumentar cantidad"
                                onClick={() =>
                                  setCarrito((prev) =>
                                    prev.map((it, idx) =>
                                      idx === i
                                        ? {
                                            ...it,
                                            cantidad:
                                              Number(it.cantidad ?? 1) + 1,
                                          }
                                        : it
                                    )
                                  )
                                }
                              >
                                {/* icon plus */}
                                <svg
                                  aria-hidden="true"
                                  focusable="false"
                                  viewBox="0 0 12 12"
                                  width="10"
                                  className="icon icon-plus"
                                >
                                  <path
                                    d="M6 0v12M0 6h12"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                  ></path>
                                </svg>
                              </button>
                            </div>

                            {/* Quitar */}
                            <button
                              onClick={() => eliminarDelCarrito(i)}
                              className="text-xs underline text-gray-600 hover:text-gray-900"
                              aria-label={`Quitar ${item.nombre} - ${item.talle}`}
                            >
                              Quitar
                            </button>
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
              {/* ====== FIN LISTADO ====== */}

              {/* Texto de envío gratis */}
              <div className="!mt-10 text-center">
                <p className="text-sm sm:text-sm font-semibold pd-onepalm text-green-700 inline-flex items-center gap-2">
                  <PackageCheck size={20} aria-hidden="true" />
                  <span>Envío gratis </span>
                  <PackageCheck size={20} aria-hidden="true" />
                </p>
              </div>
            </>
          )}
        </div>

        {/* Totales */}
        <div className="border-t p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs pd-onepalm text-gray-700">Subtotal</span>
            <span className="text-[10px] proba text-gray-700">
              ${formatAr(isEmpty ? 0 : subtotalCalc)}
            </span>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-700">Envío</span>
            <span className="inline-flex proba items-center gap-1 text-[10px] font-semibold text-green-700">
              $0,00
            </span>
          </div>

          <div className="md:hidden mx-auto h-px w-[97%] bg-gray-300/70 !mt-2 !mb-1" />

          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-900 font-semibold">Total</span>
            <span className="text-xs proba font-semibold text-gray-700">
              ${formatAr(totalBase)}
            </span>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">En transferencia</span>
            <span className="text-xs proba font-semibold text-green-700">
              ${formatAr(totalTransfer)}
            </span>
          </div>
        </div>

        {/* Botones */}
        <div className="p-4 space-y-3 border-t">
          <button
            onClick={irACheckout}
            disabled={carrito.length === 0}
            className="w-full nav-onepalm text-xs border border-transparent bg-gray-600 text-white px-6 py-3 shadow-md hover:shadow-lg hover:bg-white hover:text-gray-600 hover:border-black active:scale-95 transition"
          >
            Iniciar compra
          </button>

          <button
            onClick={closeCart}
            className="w-full nav-onepalm text-xs text-gray-700 px-6 py-3 hover:bg-gray-100 active:scale-95 transition"
          >
            Seguir comprando
          </button>
        </div>
      </aside>
    </>
  );
}